import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  UsersIcon, 
  BuildingStorefrontIcon, 
  ChartBarIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';
import apiService from '../../services/api';
import { DateDisplay } from '../../components/Forms';

const AdminDashboard: React.FC = () => {
  // Fetch admin-specific data
  const { data: vendorStats, isLoading: vendorStatsLoading } = useQuery({
    queryKey: ['vendorStats'],
    queryFn: () => apiService.getVendorStats(),
    enabled: true
  });

  const { data: vendorsData } = useQuery({
    queryKey: ['vendors', 'overview'],
    queryFn: () => apiService.getVendors(1, 5),
    enabled: true
  });

  const stats = vendorStats || {
    total: 0,
    active: 0,
    inactive: 0,
    expiredSubscriptions: 0,
    planStats: []
  };

  const recentVendors = vendorsData?.data || [];

  const StatCard = ({ title, value, icon: Icon, color, subtitle }: any) => (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center">
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
        <div className="mr-4">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
        </div>
      </div>
    </div>
  );

  const QuickActionCard = ({ title, description, icon: Icon, action, color }: any) => (
    <div className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow cursor-pointer">
      <div className="flex items-center mb-4">
        <div className={`p-2 rounded-lg ${color} mr-3`}>
          <Icon className="h-5 w-5 text-white" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      </div>
      <p className="text-gray-600 mb-4">{description}</p>
      <button className="text-primary-600 hover:text-primary-800 font-medium">
        {action} →
      </button>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">لوحة تحكم المدير</h1>
          <p className="text-gray-600">نظرة عامة على النظام وإدارة البائعين</p>
        </div>
        <div className="flex space-x-3 space-x-reverse">
          <button className="btn-primary">
            <UsersIcon className="h-5 w-5 ml-2" />
            إضافة بائع جديد
          </button>
          <button className="btn-secondary">
            <ChartBarIcon className="h-5 w-5 ml-2" />
            تقرير شامل
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="إجمالي البائعين"
          value={stats.total}
          icon={BuildingStorefrontIcon}
          color="bg-blue-500"
        />
        <StatCard
          title="البائعون النشطون"
          value={stats.active}
          icon={CheckCircleIcon}
          color="bg-green-500"
          subtitle={`${stats.total > 0 ? Math.round((stats.active / stats.total) * 100) : 0}% من الإجمالي`}
        />
        <StatCard
          title="الاشتراكات المنتهية"
          value={stats.expiredSubscriptions}
          icon={ExclamationTriangleIcon}
          color="bg-red-500"
        />
        <StatCard
          title="البائعون غير النشطين"
          value={stats.inactive}
          icon={ClockIcon}
          color="bg-yellow-500"
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <div className="lg:col-span-1">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">إجراءات سريعة</h2>
          <div className="space-y-4">
            <QuickActionCard
              title="إدارة البائعين"
              description="إضافة أو تعديل أو حذف البائعين"
              icon={UsersIcon}
              action="إدارة البائعين"
              color="bg-blue-500"
            />
            <QuickActionCard
              title="التقارير المالية"
              description="عرض الإحصائيات المالية للنظام"
              icon={CurrencyDollarIcon}
              action="عرض التقارير"
              color="bg-green-500"
            />
            <QuickActionCard
              title="إعدادات النظام"
              description="تكوين إعدادات النظام العامة"
              icon={GlobeAltIcon}
              action="الإعدادات"
              color="bg-purple-500"
            />
          </div>
        </div>

        {/* Recent Vendors */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">أحدث البائعين</h2>
            </div>
            <div className="p-6">
              {recentVendors.length === 0 ? (
                <div className="text-center py-8">
                  <BuildingStorefrontIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">لا يوجد بائعون مسجلون</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {recentVendors.map((vendor: any) => (
                    <div key={vendor._id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                      <div className="flex items-center">
                        <div className={`w-3 h-3 rounded-full mr-3 ${vendor.isActive ? 'bg-green-400' : 'bg-red-400'}`}></div>
                        <div>
                          <h3 className="font-medium text-gray-900">{vendor.name}</h3>
                          <p className="text-sm text-gray-500">{vendor.companyName}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          vendor.subscription.plan === 'premium' 
                            ? 'bg-purple-100 text-purple-800'
                            : vendor.subscription.plan === 'enterprise'
                            ? 'bg-indigo-100 text-indigo-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {vendor.subscription.plan === 'premium' ? 'مميز' :
                           vendor.subscription.plan === 'enterprise' ? 'مؤسسات' : 'أساسي'}
                        </span>
                        <p className="text-xs text-gray-500 mt-1">
                          ينتهي في <DateDisplay value={vendor.subscription.endDate} className="inline-flex" />
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Plan Statistics */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">إحصائيات الخطط</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {stats.planStats?.map((plan: any) => (
              <div key={plan._id} className="text-center">
                <div className="text-2xl font-bold text-gray-900">{plan.count}</div>
                <div className="text-sm text-gray-500">
                  {plan._id === 'premium' ? 'خطة مميزة' :
                   plan._id === 'enterprise' ? 'خطة مؤسسات' : 'خطة أساسية'}
                </div>
              </div>
            )) || (
              <>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">0</div>
                  <div className="text-sm text-gray-500">خطة أساسية</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">0</div>
                  <div className="text-sm text-gray-500">خطة مميزة</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">0</div>
                  <div className="text-sm text-gray-500">خطة مؤسسات</div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard; 